import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import es from './locales/es.json';

// Supported languages
export const SUPPORTED_LANGUAGES = ['en', 'es'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: 'English',
  es: 'Español',
};

// Custom language detector that checks URL path first
const pathLanguageDetector = {
  name: 'path',
  lookup() {
    const path = window.location.pathname;
    const match = path.match(/^\/(es|en)(\/|$)/);
    if (match) {
      return match[1];
    }
    return undefined;
  },
};

// Custom language detector for query params
const queryLanguageDetector = {
  name: 'querystring',
  lookup() {
    const params = new URLSearchParams(window.location.search);
    const lang = params.get('lang');
    if (lang && SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)) {
      return lang;
    }
    return undefined;
  },
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(pathLanguageDetector);
languageDetector.addDetector(queryLanguageDetector);

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    detection: {
      order: ['path', 'querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: 'fairday-language',
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    react: {
      useSuspense: false,
    },
  });

// Helper to get URL with language prefix
export const getLocalizedPath = (path: string, lang: SupportedLanguage): string => {
  // Remove existing language prefix if present
  const cleanPath = path.replace(/^\/(en|es)(\/|$)/, '/');

  // English is the default, no prefix needed
  if (lang === 'en') {
    return cleanPath || '/';
  }

  // Add language prefix for non-English
  return `/${lang}${cleanPath === '/' ? '' : cleanPath}`;
};

// Helper to remove language prefix from path
export const getPathWithoutLang = (path: string): string => {
  return path.replace(/^\/(en|es)(\/|$)/, '/') || '/';
};

// Helper to get current language from URL
export const getLanguageFromPath = (): SupportedLanguage => {
  const path = window.location.pathname;
  const match = path.match(/^\/(es|en)(\/|$)/);
  if (match && SUPPORTED_LANGUAGES.includes(match[1] as SupportedLanguage)) {
    return match[1] as SupportedLanguage;
  }
  return 'en';
};

export default i18n;
